// src/components/digital_twin/TimeSlider.js
// --- UPDATE (Digital Twin MOW+ v1) ---------------------------------------------------------
//   FIXES
//   • "now" was frozen when the page opened — after an hour the slider's right end (and "Live")
//     was an hour old. The window end moves forward every 30 s while live.
//   • Dragging called onTimeChange on every pixel, so the page fired dozens of historical
//     requests per drag (responses could arrive out of order). The label follows the thumb;
//     the time is reported when the drag ends (onChangeEnd), and play-back is throttled.
//   • onTimeChange was called inside a setState updater (side effect; runs twice in StrictMode).
//   • The slider stayed in the past after the page went live from elsewhere (Data Overlay
//     "Go live") — it now jumps back to the end when isLive becomes true; moving it to the end
//     goes live instead of requesting a "historical" snapshot of now.
//   FEATURES
//   • Range selector (6 h / 24 h / 3 days / 7 days), event markers on the track (`events`:
//     [{ timestamp, level, label }] — e.g. alerts), keyboard (← → step, Space play, L live),
//     `loading` indicator while a snapshot is fetched.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
  Text, Flex, IconButton, Tooltip, HStack, Badge, Button,
  useColorModeValue, SliderMark, Menu, MenuButton, MenuList, MenuItem, Icon,
  Divider 
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FaPlay, FaPause, FaStepBackward, FaStepForward,
  FaBackward, FaForward, FaHistory, FaClock, FaTachometerAlt,
  FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);

// ==================== ANIMATIONS ====================
const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(72, 187, 120, 0); }
  100% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
`;

// ==================== DEBUG LOGGER ====================
const logger = {
  log: (...args) => console.log('[TIME_SLIDER]', ...args),
  warn: (...args) => console.warn('[TIME_SLIDER]', ...args),
};

const TimeSlider = ({
  onTimeChange,
  onGoLive,
  hoursBack = 24,
  stepMinutes = 15,
  autoPlaySpeed = 1000, // Base ms between steps
  showMarks = true,
  isLive = false,
  hidden = false, // NEW: when true, hides the entire component (used to hide when DataOverlay is expanded)
  // --- UPDATE
  events = [],
  loading = false,
  rangeOptions = [6, 24, 72, 168],
  onRangeChange = null,
  // --- END UPDATE
}) => {
  const [sliderValue, setSliderValue] = useState(100);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedMultiplier, setSpeedMultiplier] = useState(1);
  const [isMinimized, setIsMinimized] = useState(false);
  const playIntervalRef = useRef(null);
  // --- UPDATE: moving window end, selectable range, latest callbacks
  const [hoursBackState, setHoursBackState] = useState(hoursBack);
  useEffect(() => { setHoursBackState(hoursBack); }, [hoursBack]);
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!isLive) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, [isLive]);
  const onTimeChangeRef = useRef(onTimeChange); onTimeChangeRef.current = onTimeChange;
  const lastEmitRef = useRef(0);
  const goingLiveRef = useRef(false);
  // --- END UPDATE

  // ==================== THEME HOOKS ====================
  const panelBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(26, 32, 44, 0.85)');
  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const trackBg = useColorModeValue('gray.200', 'whiteAlpha.300');
  const filledTrackBg = useColorModeValue('purple.500', 'purple.400');
  const minimizedBg = useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(45, 55, 72, 0.95)');
  const historyIconBg = useColorModeValue('purple.100', 'purple.900');
  const menuListBg = useColorModeValue('white', 'gray.800');
  // --- UPDATE
  const eventTickBg = useColorModeValue('gray.700', 'whiteAlpha.800');
  // --- END UPDATE

  // ==================== TIME CALCULATIONS ====================
  const { now, startTime, smartMarks } = useMemo(() => {
    const nowTime = new Date(nowTick); // --- UPDATE: moving window end (was new Date() once)
    const start = new Date(nowTime.getTime() - hoursBackState * 60 * 60 * 1000); // --- UPDATE: selectable range
    
    // Generate clean, evenly spaced marks (0%, 25%, 50%, 75%, 100%) to prevent overlapping
    const marks = [0, 25, 50, 75, 100].map(percent => {
      const time = new Date(start.getTime() + (percent / 100) * (nowTime.getTime() - start.getTime()));
      return { value: percent, time };
    });
    
    return { now: nowTime, startTime: start, smartMarks: marks };
  }, [hoursBackState, nowTick]); // --- UPDATE

  // ==================== TIMESTAMP CONVERSION ====================
  const getTimestampFromSlider = useCallback((value) => {
    const percent = value / 100;
    return new Date(startTime.getTime() + percent * (now.getTime() - startTime.getTime()));
  }, [startTime, now]);

  const getSliderFromTimestamp = useCallback((timestamp) => {
    const percent = (timestamp.getTime() - startTime.getTime()) / (now.getTime() - startTime.getTime());
    return Math.max(0, Math.min(100, percent * 100));
  }, [startTime, now]);

  // ==================== HANDLERS ====================
  const handleSliderChange = useCallback((value) => {
    setSliderValue(value);
    const timestamp = getTimestampFromSlider(value);
    // --- UPDATE: the end of the track is "live"
    if (value >= 100 && onGoLive && !isLive) { if (!goingLiveRef.current) onGoLive(); return; }
    if (value >= 100 && isLive) return;
    // --- END UPDATE
    onTimeChange?.(timestamp);
  }, [getTimestampFromSlider, onTimeChange, onGoLive, isLive]);

  // --- UPDATE: drag shows the time; the request goes out when the drag ends
  const handleDrag = useCallback((value) => { setSliderValue(value); }, []);
  const handleDragEnd = useCallback((value) => { handleSliderChange(value); }, [handleSliderChange]);
  // keyboard on the thumb: Chakra does not reliably fire onChangeEnd for keys — report on key up
  const sliderValueRef = useRef(sliderValue); sliderValueRef.current = sliderValue;
  const handleSliderKeyUp = useCallback((e) => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) handleSliderChange(sliderValueRef.current);
  }, [handleSliderChange]);
  // jump back to the end when the page goes live from elsewhere
  useEffect(() => { if (isLive) { setSliderValue(100); setIsPlaying(false); } }, [isLive]);
  const changeRange = useCallback((h) => { setHoursBackState(h); onRangeChange?.(h); }, [onRangeChange]);
  // --- END UPDATE

  const handleStepBackward = useCallback(() => {
    const currentTime = getTimestampFromSlider(sliderValue);
    const newTime = new Date(currentTime.getTime() - stepMinutes * 60 * 1000);
    if (newTime >= startTime) {
      handleSliderChange(getSliderFromTimestamp(newTime));
    } else {
      handleSliderChange(0);
    }
  }, [sliderValue, stepMinutes, startTime, getTimestampFromSlider, getSliderFromTimestamp, handleSliderChange]);

  const handleStepForward = useCallback(() => {
    const currentTime = getTimestampFromSlider(sliderValue);
    const newTime = new Date(currentTime.getTime() + stepMinutes * 60 * 1000);
    if (newTime <= now) {
      handleSliderChange(getSliderFromTimestamp(newTime));
    } else {
      handleSliderChange(100);
    }
  }, [sliderValue, stepMinutes, now, getTimestampFromSlider, getSliderFromTimestamp, handleSliderChange]);

  const handleTogglePlay = useCallback(() => setIsPlaying(prev => !prev), []);
  const handleGoToStart = useCallback(() => handleSliderChange(0), [handleSliderChange]);
  const handleGoToEnd = useCallback(() => handleSliderChange(100), [handleSliderChange]);

  const handleGoLive = useCallback(() => {
    goingLiveRef.current = true; // --- UPDATE: onGoLive is called once, below
    handleSliderChange(100);
    goingLiveRef.current = false; // --- UPDATE
    setIsPlaying(false);
    setSpeedMultiplier(1);
    onGoLive?.();
  }, [handleSliderChange, onGoLive]);

  // ==================== AUTO PLAY ====================
  useEffect(() => {
    if (isPlaying) {
      const activeInterval = autoPlaySpeed / speedMultiplier;
      
      // --- UPDATE: pure updater; the time is reported outside it (throttled to 1 per second)
      playIntervalRef.current = setInterval(() => {
        setSliderValue(prev => {
          const stepIncrement = speedMultiplier > 2 ? 0.5 : 0.1;
          return Math.min(100, prev + stepIncrement);
        });
      }, activeInterval);
      // --- END UPDATE

      return () => {
        if (playIntervalRef.current) clearInterval(playIntervalRef.current);
      };
    }
  }, [isPlaying, autoPlaySpeed, speedMultiplier]); // --- UPDATE: no callbacks inside the interval

  // --- UPDATE: report play-back position (throttled) and stop at the end
  useEffect(() => {
    if (!isPlaying) return;
    if (sliderValue >= 100) { setIsPlaying(false); onTimeChangeRef.current?.(getTimestampFromSlider(100)); return; }
    const t = Date.now();
    if (t - lastEmitRef.current < 1000) return;
    lastEmitRef.current = t;
    onTimeChangeRef.current?.(getTimestampFromSlider(sliderValue));
  }, [isPlaying, sliderValue, getTimestampFromSlider]);
  // keyboard: ← → step, space play / pause, L live (not while typing)
  useEffect(() => {
    if (hidden || isMinimized) return undefined;
    const onKey = (e) => {
      const tg = e.target; if (tg && (tg.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(tg.tagName))) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); handleStepBackward(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); handleStepForward(); }
      else if (e.key === ' ' && tg?.getAttribute?.('role') !== 'slider' && tg?.tagName !== 'BUTTON') { e.preventDefault(); setIsPlaying((p) => !p); }
      else if (e.key.toLowerCase() === 'l') { e.preventDefault(); handleGoLive(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  // event markers within the window
  const eventMarks = useMemo(() => (events || []).map((ev) => {
    const ms = typeof ev.timestamp === 'number' ? ev.timestamp : Date.parse(ev.timestamp ?? ev.created_at ?? ev.time);
    if (!Number.isFinite(ms)) return null;
    const v = ((ms - startTime.getTime()) / (now.getTime() - startTime.getTime())) * 100;
    return v >= 0 && v <= 100 ? { v, level: ev.level || ev.severity || 'info', label: ev.label || ev.message || ev.alert_message || 'Event', ms } : null;
  }).filter(Boolean).slice(0, 200), [events, startTime, now]);
  // --- END UPDATE

  // ==================== FORMATTED TEXT ====================
  const formattedCurrentTime = useMemo(() => {
    const time = getTimestampFromSlider(sliderValue);
    return time.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }, [sliderValue, getTimestampFromSlider]);

  const timeDifference = useMemo(() => {
    const currentTime = getTimestampFromSlider(sliderValue);
    const diffMs = now.getTime() - currentTime.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) return `-${diffHours}h ${diffMins}m`;
    if (diffMins > 0) return `-${diffMins}m`;
    return 'Live';
  }, [sliderValue, now, getTimestampFromSlider]);

  // NEW: hide entirely when DataOverlay is expanded
  if (hidden) return null;

  return (
    <AnimatePresence mode="wait">
      {isMinimized ? (
        // ==================== MINIMIZED PILL STATE ====================
        <MotionBox
          key="minimized-timeline"
          initial={{ opacity: 0, y: 20, x: "-50%" }}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: 20, x: "-50%" }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          position="absolute"
          // FIX: Pushed up to 130px to fully clear the System Overview footer
          bottom={{ base: '100px', md: '130px' }} 
          left="50%"
          zIndex={20}
        >
          <Button
            leftIcon={<FaHistory />}
            rightIcon={<FaChevronUp />}
            colorScheme="purple"
            variant="solid"
            onClick={() => setIsMinimized(false)}
            boxShadow="0 10px 30px rgba(0, 0, 0, 0.3)"
            borderRadius="full"
            bg={minimizedBg}
            color={textColor}
            borderWidth="1px"
            borderColor={panelBorder}
            backdropFilter="blur(16px)"
            px={6}
            _hover={{ transform: 'scale(1.05)' }}
          >
            Open Timeline
            {!isLive && (
              <Badge colorScheme="orange" ml={3} borderRadius="full" variant="subtle">
                {timeDifference}
              </Badge>
            )}
          </Button>
        </MotionBox>
      ) : (
        // ==================== EXPANDED PANEL STATE ====================
        <MotionBox
          key="expanded-timeline"
          initial={{ opacity: 0, y: 20, x: "-50%" }}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: 20, x: "-50%" }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          position="absolute"
          // FIX: Pushed up to 130px to fully clear the System Overview footer
          bottom={{ base: '100px', md: '130px' }} 
          left="50%"
          bg={panelBg}
          backdropFilter="blur(16px)"
          p={5}
          borderRadius="2xl"
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.3)"
          w={{ base: '95%', md: '80%', lg: '700px' }} 
          zIndex={20}
          borderWidth="1px"
          borderColor={panelBorder}
        >
          {/* Header */}
          <Flex justify="space-between" align="center" mb={4}>
            <HStack spacing={2}>
              <Flex p={1.5} bg={historyIconBg} borderRadius="md">
                <Icon as={FaHistory} color="purple.500" boxSize={3} />
              </Flex>
              <Text fontWeight="800" fontSize="sm" color={textColor} textTransform="uppercase" letterSpacing="wide">
                Historical Telemetry
              </Text>
            </HStack>
            
            <HStack spacing={3}>
              {/* --- UPDATE: range selector + loading */}
              {loading && <Badge colorScheme="purple" variant="subtle" borderRadius="full">loading…</Badge>}
              <Menu placement="bottom-end">
                <MenuButton as={Button} size="xs" variant="ghost" color={mutedText} rightIcon={<FaChevronDown />} aria-label="Timeline range">{hoursBackState >= 48 ? `${Math.round(hoursBackState / 24)} days` : `${hoursBackState} h`}</MenuButton>
                <MenuList bg={menuListBg} minW="120px" zIndex={30}>
                  {rangeOptions.map((h) => <MenuItem key={h} onClick={() => changeRange(h)} fontWeight={h === hoursBackState ? '800' : '500'}>{h >= 48 ? `Last ${Math.round(h / 24)} days` : `Last ${h} hours`}</MenuItem>)}
                </MenuList>
              </Menu>
              {/* --- END UPDATE */}
              <Badge 
                colorScheme={isLive ? 'green' : 'orange'} 
                variant="subtle" 
                px={2} py={1} 
                borderRadius="full" 
                display="flex" 
                alignItems="center"
              >
                {isLive && <Box as="span" w="2" h="2" borderRadius="full" bg="green.500" mr={2} animation={`${pulseRing} 2s infinite`} />}
                {isLive ? 'LIVE' : timeDifference}
              </Badge>
              
              {!isLive && (
                <Button size="xs" colorScheme="green" leftIcon={<FaClock />} onClick={handleGoLive} borderRadius="full" boxShadow="sm">
                  Sync to Live
                </Button>
              )}

              {/* Collapse Button */}
              <Tooltip label="Hide Timeline" hasArrow>
                <IconButton 
                  icon={<FaChevronDown />} 
                  size="sm" 
                  variant="ghost" 
                  color={mutedText} 
                  _hover={{ color: textColor, bg: trackBg }}
                  onClick={() => setIsMinimized(true)}
                  aria-label="Minimize Timeline"
                />
              </Tooltip>
            </HStack>
          </Flex>

          {/* Current Time Display */}
          <Text textAlign="center" fontSize="2xl" fontWeight="900" color={textColor} mb={6} letterSpacing="tight">
            {formattedCurrentTime}
          </Text>

          {/* Slider */}
          <Box px={3} mb={showMarks ? 8 : 4}>
            <Slider value={sliderValue} onChange={handleDrag} onChangeEnd={handleDragEnd} onKeyUp={handleSliderKeyUp} min={0} max={100} step={0.1} focusThumbOnChange={false} aria-label="Timeline position">{/* --- UPDATE: request on drag end */}
              {/* --- UPDATE: event markers */}
              {eventMarks.map((m) => (
                <SliderMark key={`${m.ms}-${m.v}`} value={m.v} mt="-14px" ml="-1px" w="2px" h="8px" borderRadius="sm" bg={m.level === 'critical' ? 'red.400' : m.level === 'warning' ? 'orange.400' : eventTickBg} title={`${new Date(m.ms).toLocaleString()} · ${m.label}`} />
              ))}
              {/* --- END UPDATE */}
              {showMarks && smartMarks.map(point => (
                <SliderMark key={point.value} value={point.value} mt={3} ml={-5} fontSize="xs" fontWeight="600" color={mutedText}>
                  {point.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </SliderMark>
              ))}
              <SliderTrack bg={trackBg} h="6px" borderRadius="full">
                <SliderFilledTrack bg={filledTrackBg} />
              </SliderTrack>
              <Tooltip label={formattedCurrentTime} placement="top" hasArrow bg="purple.600">
                <SliderThumb boxSize={6} bg={filledTrackBg} boxShadow="md" _focus={{ boxShadow: 'outline' }}>
                  <Icon as={FaClock} color="white" boxSize={3} />
                </SliderThumb>
              </Tooltip>
            </Slider>
          </Box>

          {/* Playback Controls */}
          <Flex justify="center" align="center" mt={2} gap={4}>
            <Tooltip label={`Jump to Start (-${hoursBackState}h)`} hasArrow>{/* --- UPDATE: real range */}
              <IconButton icon={<FaStepBackward />} size="sm" variant="ghost" color={mutedText} _hover={{ color: textColor, bg: trackBg }} onClick={handleGoToStart} aria-label="Jump to start" isDisabled={sliderValue === 0} />
            </Tooltip>

            <Tooltip label={`Step Back ${stepMinutes} min`} hasArrow>
              <IconButton icon={<FaBackward />} size="md" variant="ghost" color={mutedText} _hover={{ color: textColor, bg: trackBg }} onClick={handleStepBackward} aria-label="Step backward" isDisabled={sliderValue === 0} />
            </Tooltip>

            <Tooltip label={isPlaying ? 'Pause Simulation' : 'Play Timeline'} hasArrow>
              <IconButton
                icon={isPlaying ? <FaPause /> : <FaPlay />}
                size="lg"
                colorScheme="purple"
                borderRadius="full"
                boxShadow="lg"
                onClick={handleTogglePlay}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                isDisabled={sliderValue >= 100}
                _hover={{ transform: 'scale(1.05)' }}
                _active={{ transform: 'scale(0.95)' }}
                transition="all 0.2s"
              />
            </Tooltip>

            <Tooltip label={`Step Forward ${stepMinutes} min`} hasArrow>
              <IconButton icon={<FaForward />} size="md" variant="ghost" color={mutedText} _hover={{ color: textColor, bg: trackBg }} onClick={handleStepForward} aria-label="Step forward" isDisabled={sliderValue >= 100} />
            </Tooltip>

            <Tooltip label="Jump to Live" hasArrow>
              <IconButton icon={<FaStepForward />} size="sm" variant="ghost" color={mutedText} _hover={{ color: textColor, bg: trackBg }} onClick={handleGoToEnd} aria-label="Jump to end" isDisabled={sliderValue >= 100} />
            </Tooltip>

            <Divider orientation="vertical" h="24px" borderColor={panelBorder} mx={2} />

            {/* Speed Selector */}
            <Menu placement="top">
              <Tooltip label="Playback Speed" hasArrow>
                <MenuButton as={Button} size="sm" variant="ghost" color={mutedText} _hover={{ color: textColor, bg: trackBg }} leftIcon={<FaTachometerAlt />}>
                  {speedMultiplier}x
                </MenuButton>
              </Tooltip>
              <MenuList bg={menuListBg} borderColor={panelBorder} minW="100px" shadow="xl">
                {[1, 2, 5, 10].map(speed => (
                  <MenuItem key={speed} onClick={() => setSpeedMultiplier(speed)} fontWeight={speedMultiplier === speed ? '800' : '500'} color={speedMultiplier === speed ? 'purple.500' : textColor}>
                    {speed}x Speed
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
        </MotionBox>
      )}
    </AnimatePresence>
  );
};

export default TimeSlider;